import { defineNuxtPlugin } from 'nuxt/app';
import * as Sentry from '@sentry/vue';
import { isSpanContextValid, trace } from '@opentelemetry/api';
import config from '../../config.json';

// v10（@sentry/core 10.74.0）が sendDefaultPii 未設定時に deny に使っていた内部定数 PII_HEADER_SNIPPETS の値。v11 で削除された。
const V10_PII_SNIPPETS = ['forwarded', '-ip', 'remote-', 'via', '-user'];

// v10 は MCP の入出力を既定で送らなかった。mcp キーを持たない SDK の型でも excess property にならないよう変数経由で spread する（FCDS-6535）。
const V10_MCP_DATA_COLLECTION = { mcp: { inputs: false, outputs: false } };

export default defineNuxtPlugin((nuxtApp) => {
  const { vueApp, $logger } = nuxtApp;

  if (!config.DATASTORE_UI_SENTRY_DSN) {
    if ($logger) {
      $logger.warn('Sentry DSN not set, skipping Sentry initialization');
    }
    return;
  }

  Sentry.init({
    app: vueApp,
    dsn: config.DATASTORE_UI_SENTRY_DSN,
    environment: config.SENTRY_ENVIRONMENT,
    release: config.SENTRY_RELEASE,
    initialScope: {
      tags: { app: 'datastore-ui-v3' },
    },
    // トレーシング / 伝播は OpenTelemetry に一本化し、Sentry はエラー捕捉専用とする
    // (FCDS-5453 / 06 §1.1)。browserTracingIntegration と tracePropagationTargets は
    // 二重伝播 (sentry-trace/baggage) を避けるため撤去し、tracesSampleRate は 0 に固定。
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0,
    // v11 で既定が true になった。プリミティブ値や stack から frame を取れない DOMException の capture に
    // 合成 stack が付き、grouping が v10 の issue から分かれるため v10 の挙動に揃える (FCDS-6387)。
    attachStacktrace: false,
    // v11 は dataCollection を省くと userInfo 等を既定で収集し、エンドユーザーの IP を Sentry が保存する。既定が広がった
    // キーを v10 の sendDefaultPii 未設定時の値に戻す（FCDS-6385）。breadcrumb の URL は urlQueryParams の対象外。
    dataCollection: {
      userInfo: false,
      cookies: { deny: V10_PII_SNIPPETS },
      httpHeaders: { deny: V10_PII_SNIPPETS },
      httpBodies: [],
      urlQueryParams: { deny: V10_PII_SNIPPETS },
      genAI: { inputs: false, outputs: false },
      ...V10_MCP_DATA_COLLECTION,
      databaseQueryData: false,
    },
    // captureException 時に active な OTel span の trace_id をタグ付与し、
    // Sentry のエラー → Grafana(Tempo)の trace へ遷移できるようにする (06 §1.2)。
    beforeSend: (event) => {
      const spanContext = trace.getActiveSpan()?.spanContext();
      if (spanContext && isSpanContextValid(spanContext)) {
        event.tags = { ...event.tags, trace_id: spanContext.traceId };
      }
      return event;
    },
    // Session Replay
    replaysSessionSampleRate: config.SENTRY_REPLAYS_SESSION_SAMPLE_RATE, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: config.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
});
